import User from '@/services/User/User';
import * as types from '../mutation-types';
import dayjs from 'dayjs';

// state
export const state = {
  user: null,
  token: null,
  expiresIn: null,
  redirect: null,
  clientId: null,
  api: {
    token: null,
    expiresIn: null,
  },
};

// Derive the real expiry from the access token's `exp` claim. Callers used to
// pass a hardcoded `expiresIn: 86400` that SAVE_TOKEN then treated as *minutes*
// (`add(86400, 'minute')` ≈ 60 days), so the router's expiry guard never fired
// while the real token expired in hours — the token silently died mid-session,
// the next request 401'd, and the user was bounced back to Auth0. Reading the
// JWT exp is authoritative; the numeric arg is kept only as a seconds fallback
// for non-JWT tokens.
const resolveExpiry = (token, fallbackSeconds) => {
  try {
    // JWT segments are base64url (`-`/`_`, no padding); atob only accepts
    // standard base64, so translate before decoding or valid tokens throw.
    const segment = token.split('.')[1];
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const { exp } = JSON.parse(atob(padded));
    if (exp) {
      return dayjs.unix(exp).toDate();
    }
  } catch (e) {
    // Not a decodable JWT — fall through to the seconds fallback.
  }
  return dayjs()
    .add(Number(fallbackSeconds), 'second')
    .toDate();
};

// mutations
export const mutations = {
  [types.SAVE_TOKEN](state, { token, expiresIn, clientId }) {
    state.token = token;
    state.expiresIn = resolveExpiry(token, expiresIn);
    state.clientId = clientId;
  },

  [types.FETCH_USER_SUCCESS](state, { user }) {
    state.user = user;
  },

  [types.FETCH_USER_FAILURE](state) {
    state.token = null;
  },

  [types.LOGOUT](state) {
    state.user = null;
    state.token = null;
    state.expiresIn = null;
    state.clientId = null;
  },

  [types.LOGIN_FAILURE](state) {
    state.user = null;
    state.token = null;
    state.expiresIn = null;
    state.clientId = null;
  },

  [types.UPDATE_USER](state, { user }) {
    state.user = user;
  },

  [types.SET_API_ACCESS_TOKEN](state, { token, expiresIn }) {
    state.api.token = token;
    const expiry = dayjs()
      .add(Number(expiresIn), 'minute')
      .toDate();
    state.api.expiresIn = expiry;
  },

  [types.SET_ORIGINAL_DESTINATION](state, redirect) {
    state.redirect = redirect;
  },
};

// actions
export const actions = {
  saveToken({ commit }, payload) {
    return new Promise((resolve) => {
      commit(types.SAVE_TOKEN, payload);
      resolve();
    });
  },

  async refreshToken({ commit }) {
    try {
      const { data } = await User.refresh();

      commit(types.SAVE_TOKEN, data);
    } catch (e) {

    }
  },

  async refreshApiToken({ commit, getters }) {
    try {
      const { data } = await User.getApiToken(getters.apiToken);

      commit(types.SET_API_ACCESS_TOKEN, data.data);
    } catch (e) {

    }
  },

  async fetchUser({ commit }) {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await User.me();

        const { user } = data;

        commit(types.FETCH_USER_SUCCESS, { user });
        resolve(user);
      } catch (e) {
        commit(types.FETCH_USER_FAILURE);
        reject(e);
      }
    });
  },

  async updateUser({ commit }, payload) {
    commit(types.UPDATE_USER, payload);
  },

  async logout({ commit, state }, { expired = false } = {}) {
    const { token } = state;

    try {
      // If the user is manually logging out
      this._vm.$auth.logout({ logoutParams: { returnTo: window.location.origin } });
    } finally {
      commit(types.LOGOUT);
      commit(types.RESET_ACTION);
      commit(types.RESET_TAGS);
      commit(types.RESET_COUNTS);
    }
  },

  setRedirect({ commit, state, dispatch }, redirect) {
    commit(types.SET_ORIGINAL_DESTINATION, redirect);
  },
};

// getters
export const getters = {
  authUser: state => state.user,
  authToken: state => state.token,
  authCheck: state => state.user !== null,
  authExpiry: state => state.expiresIn,
  apiToken: state => state.api.token,
  redirect: state => state.redirect,
  permissions: state => (state.user ? state.user.roles.flatMap(r => r.permissions.map(p => p.name)) : []),
  userClientId: (state) => {
    if (state.user && state.user.clients && state.user.clients.length) {
      const selectedClient = state.user.clients
        .find(client => state.user.currentClientId === client.clientId);

      if (!selectedClient) {
        return null;
      }

      return selectedClient.clientId;
    }
    return null;
  },
  userClient: (state) => {
    if (state.user && state.user.clients && state.user.clients.length && state.user.currentClientId) {
      return state.user.clients
        .find(client => state.user.currentClientId === client.clientId);
    }
    return null;
  },
  userClientName: (state) => {
    if (state.user && state.user.clients && state.user.clients.length) {
      const selectedClient = state.user.clients
        .find(client => state.user.currentClientId === client.clientId);
      return selectedClient.client;
    }

    return null;
  },
  userClientHasSection156Active: (state) => {
    if (state.user && state.user.clients && state.user.clients.length) {
      const selectedClient = state.user.clients
        .find(client => state.user.currentClientId === client.clientId);
      return selectedClient.section156Active === 1;
    }

    return false;
  },
  userFullName: (state) => {
    if (state.user) {
      return `${state.user.firstName} ${state.user.lastName}`;
    }
    return '';
  },
  userAcceptedTerms: (state) => {
    if (state.user) {
      return state.user.termsAccepted;
    }
    return false;
  },
  clients: (state) => {
    if (!state.user || !state.user.clients || state.user.clients.length === 0) {
      return [];
    }
    return [...state.user.clients].sort((a, b) => a.client.localeCompare(b.client));
  },
};
